import React from 'react';

/* ━━━ 1. Pyramid Layers ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface PyramidLayersProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const PyramidLayers: React.FC<PyramidLayersProps> = ({
  highlightTarget, labels = ['Foundation','Middle','Peak'], accentColor = '#f59e0b', opacity = 1,
}) => {
  const n = labels.length, baseW = 500, peakW = 80, cx = 400, topY = 60, totalH = 360;
  const layerH = totalH / n;
  return (
    <g opacity={opacity}>
      <defs><filter id="py-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter></defs>
      {labels.map((label, i) => {
        const ri = n - 1 - i; // reverse: bottom = index 0
        const tw = peakW + (baseW - peakW) * (ri / n);
        const bw = peakW + (baseW - peakW) * ((ri + 1) / n);
        const y = topY + i * layerH;
        const hi = highlightTarget === `layer_${i+1}`;
        const shade = 0.5 + (i / n) * 0.4;
        return <g key={i} filter="url(#py-sh)">
          {/* 3D right face */}
          <path d={`M${cx+tw/2},${y} L${cx+bw/2},${y+layerH} L${cx+bw/2+12},${y+layerH-6} L${cx+tw/2+12},${y-6}Z`} fill="rgba(0,0,0,0.25)"/>
          {/* 3D top face */}
          <path d={`M${cx-tw/2},${y} L${cx+tw/2},${y} L${cx+tw/2+12},${y-6} L${cx-tw/2+12},${y-6}Z`} fill={accentColor} opacity={shade * 0.6}/>
          {/* Front face */}
          <path d={`M${cx-tw/2},${y} L${cx+tw/2},${y} L${cx+bw/2},${y+layerH} L${cx-bw/2},${y+layerH}Z`} fill={accentColor} opacity={shade} stroke={hi?'#fff':'rgba(255,255,255,0.12)'} strokeWidth={hi?2.5:1}/>
          <line x1={cx-tw/2+8} y1={y+3} x2={cx+tw/2-8} y2={y+3} stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
          <text x={cx} y={y+layerH/2+5} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="11" fontFamily="'Inter', system-ui, sans-serif" fontWeight="600">{label}</text>
        </g>;
      })}
    </g>
  );
};

/* ━━━ 2. Tree Diagram ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface TreeDiagramProps {
  highlightTarget?: string;
  labels?: Record<string, string> | string[];
  accentColor?: string;
  opacity?: number;
}
export const TreeDiagram: React.FC<TreeDiagramProps> = ({
  highlightTarget, labels, accentColor = '#10b981', opacity = 1,
}) => {
  const arr = Array.isArray(labels) ? labels : [labels?.root||'Root', labels?.left||'Left', labels?.right||'Right', 'L-Child', 'R-Child'];
  // Layout: root → 2 children → 2 grandchildren
  const nodes = [
    { x: 400, y: 80, label: arr[0], id: 'root', children: [1, 2] },
    { x: 260, y: 200, label: arr[1], id: 'child_1', children: [3] },
    { x: 540, y: 200, label: arr[2], id: 'child_2', children: [4] },
    { x: 200, y: 320, label: arr[3] || '', id: 'leaf_1', children: [] },
    { x: 540, y: 320, label: arr[4] || '', id: 'leaf_2', children: [] },
  ];
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="td-nd" cx="35%" cy="30%"><stop offset="0%" stopColor="#e2e8f0"/><stop offset="100%" stopColor={accentColor}/></radialGradient>
        <filter id="td-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {/* Edges */}
      {nodes.map((n, i) => n.children.map(ci => {
        const c = nodes[ci];
        return <g key={`e${i}-${ci}`}>
          <line x1={n.x+2} y1={n.y+3} x2={c.x+2} y2={c.y+3} stroke="rgba(0,0,0,0.3)" strokeWidth="3"/>
          <line x1={n.x} y1={n.y} x2={c.x} y2={c.y} stroke={accentColor} strokeWidth="2.5" opacity="0.5"/>
        </g>;
      }))}
      {/* Nodes */}
      {nodes.map((n) => {
        if (!n.label) return null;
        const hi = highlightTarget === n.id;
        return <g key={n.id} filter="url(#td-sh)">
          <circle cx={n.x+3} cy={n.y+4} r="26" fill="rgba(0,0,0,0.3)"/>
          <circle cx={n.x} cy={n.y} r="26" fill="url(#td-nd)" stroke={hi?'#fff':accentColor} strokeWidth={hi?3:1.5}/>
          <circle cx={n.x-7} cy={n.y-9} r="5" fill="rgba(255,255,255,0.4)"/>
          <text x={n.x} y={n.y+42} textAnchor="middle" dominantBaseline="middle" fill="#e2e8f0" fontSize="10" fontFamily="'Inter', system-ui, sans-serif" fontWeight="500">{n.label}</text>
          {hi && <circle cx={n.x} cy={n.y} r="34" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></circle>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 3. Concentric Rings ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface ConcentricRingsProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  activeIndex?: number;
}
export const ConcentricRings: React.FC<ConcentricRingsProps> = ({
  highlightTarget, labels = ['Core','Inner','Middle','Outer'], accentColor = '#6366f1', opacity = 1, activeIndex = -1,
}) => {
  const cx = 400, cy = 260, maxR = 180, n = labels.length;
  return (
    <g opacity={opacity}>
      <defs><filter id="cr-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter></defs>
      {[...labels].reverse().map((label, ri) => {
        const i = n - 1 - ri;
        const r = maxR * ((i + 1) / n);
        const id = `ring_${i+1}`;
        const hi = highlightTarget === id || activeIndex === i;
        const shade = 0.2 + (i / n) * 0.4;
        return <g key={i} filter="url(#cr-sh)">
          <circle cx={cx+3} cy={cy+4} r={r} fill="none" stroke="rgba(0,0,0,0.2)" strokeWidth={hi?28:22}/>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={accentColor} strokeWidth={hi?26:20} opacity={shade}/>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
          <text x={cx+r-14} y={cy+4} fill="#e2e8f0" fontSize="11" fontFamily="'Inter', system-ui, sans-serif" fontWeight="500" textAnchor="end">{label}</text>
          {hi && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#fff" strokeWidth="2" opacity="0.6"><animate attributeName="opacity" from="0.6" to="0" dur="1.2s" repeatCount="indefinite"/></circle>}
        </g>;
      })}
      <circle cx={cx} cy={cy} r="8" fill={accentColor}/>
      <circle cx={cx-2} cy={cy-3} r="2" fill="rgba(255,255,255,0.5)"/>
    </g>
  );
};

/* ━━━ 4. Hex Grid ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface HexGridProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const HexGrid: React.FC<HexGridProps> = ({
  highlightTarget, labels = ['A','B','C','D','E','F','G'], accentColor = '#14b8a6', opacity = 1,
}) => {
  const r = 38, cx = 400, cy = 250;
  // Honeycomb positions (3 rows)
  const positions = [
    { x: cx-r*1.8, y: cy-r*1.6 }, { x: cx, y: cy-r*1.6 }, { x: cx+r*1.8, y: cy-r*1.6 },
    { x: cx-r*0.9, y: cy }, { x: cx+r*0.9, y: cy },
    { x: cx-r*1.8, y: cy+r*1.6 }, { x: cx, y: cy+r*1.6 },
  ];
  const hexPath = (hx: number, hy: number, hr: number) => {
    const pts = Array.from({length:6},(_,i)=>{const a=Math.PI/6+i*Math.PI/3;return`${hx+hr*Math.cos(a)},${hy+hr*Math.sin(a)}`;});
    return `M${pts.join(' L')}Z`;
  };
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="hx-face" x1="0" y1="0" x2="0.3" y2="1"><stop offset="0%" stopColor="#334155"/><stop offset="100%" stopColor="#0f172a"/></linearGradient>
        <filter id="hx-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {positions.slice(0, labels.length).map((p, i) => {
        const id = `hex_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={i} filter="url(#hx-sh)">
          <path d={hexPath(p.x+3, p.y+4, r)} fill="rgba(0,0,0,0.3)"/>
          <path d={hexPath(p.x, p.y, r)} fill="url(#hx-face)" stroke={hi?'#fff':accentColor} strokeWidth={hi?2.5:1.5}/>
          <path d={hexPath(p.x, p.y, r-4)} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
          {hi && <path d={hexPath(p.x, p.y, r)} fill={accentColor} opacity="0.25"/>}
          <text x={p.x} y={p.y+4} textAnchor="middle" dominantBaseline="middle" fill="#e2e8f0" fontSize="11" fontFamily="'Inter', system-ui, sans-serif" fontWeight="600">{labels[i]}</text>
        </g>;
      })}
    </g>
  );
};

/* ━━━ 5. Radial Burst ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface RadialBurstProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  centerLabel?: string;
}
export const RadialBurst: React.FC<RadialBurstProps> = ({
  highlightTarget, labels = ['A','B','C','D','E'], accentColor = '#f43e5c', opacity = 1, centerLabel = 'Core',
}) => {
  const cx = 400, cy = 260, r = 160, n = labels.length;
  const pts = labels.map((l, i) => {
    const a = (2 * Math.PI * i) / n - Math.PI / 2;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a), label: l };
  });
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="rb-center" cx="40%" cy="35%"><stop offset="0%" stopColor="#e35e8f0"/><stop offset="100%" stopColor={accentColor}/></radialGradient>
        <filter id="rb-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.4"/></filter>
      </defs>
      {/* Spokes */}
      {pts.map((p, i) => <g key={`s${i}`}>
        <line x1={cx+2} y1={cy+3} x2={p.x+2} y2={p.y+3} stroke="rgba(0,0,0,0.2)" strokeWidth="3"/>
        <line x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={accentColor} strokeWidth="2" opacity="0.4"/>
      </g>)}
      {/* Outer nodes */}
      {pts.map((p, i) => {
        const id = `spoke_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={i} filter="url(#rb-sh)">
          <circle cx={p.x+2} cy={p.y+3} r="22" fill="rgba(0,0,0,0.3)"/>
          <circle cx={p.x} cy={p.y} r="22" fill={accentColor} opacity={hi?1:0.6} stroke={hi?'#fff':'none'} strokeWidth="2"/>
          <circle cx={p.x-6} cy={p.y-7} r="4" fill="rgba(255,255,255,0.35)"/>
          <text x={p.x} y={p.y+38} textAnchor="middle" dominantBaseline="middle" fill="#e2e8f0" fontSize="10" fontFamily="'Inter', system-ui, sans-serif">{p.label}</text>
          {hi && <circle cx={p.x} cy={p.y} r="30" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></circle>}
        </g>;
      })}
      {/* Center */}
      <circle cx={cx+3} cy={cy+4} r="34" fill="rgba(0,0,0,0.3)"/>
      <circle cx={cx} cy={cy} r="34" fill="url(#rb-center)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"/>
      <circle cx={cx-10} cy={cy-12} r="6" fill="rgba(255,255,255,0.4)"/>
      <text x={cx} y={cy+5} textAnchor="middle" fill="#fff" fontSize="11" fontFamily="Inter,sans-serif" fontWeight="700">{centerLabel}</text>
    </g>
  );
};

/* ━━━ 6. Accordion ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface AccordionProps {
  highlightTarget?: string;
  labels?: string[];
  content?: string[];
  accentColor?: string;
  opacity?: number;
  activeIndex?: number;
}
export const Accordion: React.FC<AccordionProps> = ({
  highlightTarget, labels = ['Section A','Section B','Section C','Section D'], content = [], accentColor = '#0ea5e9', opacity = 1, activeIndex = 0,
}) => {
  const n = labels.length, startX = 180, w = 440, collapseH = 46, expandH = 120;
  let yAcc = 50;
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="ac-bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1e293b"/><stop offset="100%" stopColor="#0f172a"/></linearGradient>
        <filter id="ac-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {labels.map((label, i) => {
        const isExpanded = activeIndex === i;
        const h = isExpanded ? expandH : collapseH;
        const y = yAcc;
        yAcc += h + 8;
        const id = `section_${i+1}`;
        const hi = highlightTarget === id;
        const contentText = content[i] || `Item ${i + 1}`;
        return <g key={i} filter="url(#ac-sh)">
          {/* 3D offset */}
          <rect x={startX+4} y={y+3} width={w} height={h} rx="6" fill="rgba(0,0,0,0.25)"/>
          {/* Panel */}
          <rect x={startX} y={y} width={w} height={h} rx="6" fill="url(#ac-bg)" stroke={hi?'#fff':accentColor} strokeWidth={hi?2.5:1} opacity={isExpanded?1:0.75}/>
          {/* Header bar */}
          <rect x={startX} y={y} width={w} height="36" rx="6" fill={accentColor} opacity={isExpanded?0.85:0.4}/>
          <line x1={startX+8} y1={y+4} x2={startX+w-8} y2={y+4} stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
          <text x={startX+20} y={y+23} fill="#fff" fontSize="12" fontFamily="'Inter', system-ui, sans-serif" fontWeight="600">{isExpanded?'▾':'▸'} {label}</text>
          {/* Content area */}
          {isExpanded && (
            <>
              <line x1={startX+8} y1={y+38} x2={startX+w-8} y2={y+38} stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
              <text x={startX+20} y={y+63} fill="#94a3b8" fontSize="11" fontFamily="'Inter', system-ui, sans-serif">{contentText}</text>
            </>
          )}
        </g>;
      })}
    </g>
  );
};
